/**
 * Weapons (RB §5.7.3, §5.7.5, §5.4.3, §8.1).
 *
 * Diff this file against RB §5.7.3 row by row — that is what it is for.
 *
 * Two columns need reading carefully.
 *
 * **`maxRange`** is measured by `regionRange` (§10.4) — the scenario's declared
 * per-pair range, not a graph distance — and the comparison is `<=`. Range 0, a
 * launcher firing at a target in its own region, is therefore legal (decision
 * M3-D14). RB §5.7.3's "adjacent region" is a maximum, not a minimum separation;
 * the alternative leaves a battery unable to defend the ground it stands on.
 *
 * These are **defaults**. A scenario that wants a DF-21 and a DF-26 to be two
 * different IRBMs declares two asset types, each with its own `maxRange`, and the
 * launcher's type wins over the row here (§10.4.2). This table is what a scenario
 * that has said nothing plays.
 *
 * **`interceptable`** is not a property of range. RB §5.7.3 restricts the missile
 * interceptor to "anti-surface missiles inbound to the same region", so a DA-ASAT
 * — which is not anti-surface — cannot be intercepted at all.
 */
import { mwCategoryOf } from './mw-events.js';
function def(w) {
    return Object.freeze(w);
}
const WEAPON_LIST = Object.freeze([
    def({
        id: 'DA_ASAT',
        name: 'Direct-ascent ASAT',
        validTargetSegments: ['SPACE'],
        validTargetBands: ['LEO'],
        maxRange: null,
        escalation: 6,
        debrisOnKill: 2,
        interceptTn: null,
        // Not an anti-surface missile, so RB §5.7.3's interceptor cannot engage it.
        interceptable: false,
        transitPenalty: -4,
        summary: 'LEO satellites; assets in transit to or from LEO at [-4]. +2 debris in LEO.',
    }),
    def({
        id: 'SRBM',
        name: 'Short-range ballistic missile',
        validTargetSegments: ['GROUND'],
        validTargetBands: [],
        maxRange: 1,
        escalation: 5,
        debrisOnKill: 0,
        interceptTn: 11,
        interceptable: true,
        transitPenalty: 0,
        summary: 'Ground assets, adjacent region. Effect per the target damage class.',
    }),
    def({
        id: 'IRBM',
        name: 'Intermediate-range ballistic missile',
        validTargetSegments: ['GROUND'],
        validTargetBands: [],
        maxRange: 2,
        escalation: 7,
        debrisOnKill: 0,
        interceptTn: 14,
        interceptable: true,
        transitPenalty: 0,
        summary: 'Ground assets, two-region range. Effect per the target damage class.',
    }),
    def({
        id: 'HGV',
        name: 'Hypersonic glide vehicle',
        validTargetSegments: ['GROUND'],
        validTargetBands: [],
        maxRange: 2,
        escalation: 7,
        debrisOnKill: 0,
        interceptTn: 17,
        interceptable: true,
        transitPenalty: 0,
        summary: 'Ground assets, two-region range. Hardest inbound to intercept.',
    }),
    def({
        id: 'ICBM',
        name: 'Intercontinental ballistic missile',
        validTargetSegments: ['GROUND'],
        validTargetBands: [],
        // "Any range". Not `null`: null means "range is not measured this way", and an
        // ICBM's range very much is — it is simply unbounded.
        maxRange: Number.POSITIVE_INFINITY,
        escalation: 12,
        debrisOnKill: 0,
        interceptTn: 14,
        interceptable: true,
        transitPenalty: 0,
        summary: 'Ground assets at any range. The most escalatory thing in the game.',
    }),
    def({
        id: 'INTERCEPTOR',
        name: 'Missile interceptor',
        validTargetSegments: [],
        validTargetBands: [],
        // "Inbound to the same region" (RB §5.7.3) — distance 0 exactly.
        maxRange: 0,
        escalation: 0,
        debrisOnKill: 0,
        interceptTn: null,
        interceptable: false,
        transitPenalty: 0,
        summary: 'Engages anti-surface missiles inbound to its own region. Never escalatory.',
    }),
]);
export const WEAPONS = Object.freeze(Object.fromEntries(WEAPON_LIST.map((w) => [w.id, w])));
export function weaponDef(id) {
    return WEAPONS[id];
}
/**
 * Missiles committed to one interception (RB §5.7.5).
 *
 * A cap on the salvo, **not** a magazine size: errata #17 makes the magazine
 * scenario-set and this the most you may throw at one inbound. All committed
 * missiles are expended and all of them roll; the interception succeeds if any
 * single roll does.
 */
export const MAX_INTERCEPT_SALVO = 4;
/**
 * Debris from a destroyed satellite (RB §8.1).
 *
 * The LARGE-signature bonus is additive on top of the band value, and applies to
 * *any* destruction, not only a kinetic one.
 */
export const DEBRIS_ON_DESTRUCTION = Object.freeze({
    LEO: 2,
    MEO: 1,
    GEO: 1,
    HEO: 1,
});
export const DEBRIS_LARGE_SIGNATURE_BONUS = 1;
/**
 * Joint Fires engagement TN by PNT constellation health (RB §5.7.6, first of two).
 *
 * `null` is an automatic hit — deliberately not TN 1, because "no roll happens"
 * and "a roll that always succeeds" produce different `RollRecord` streams and
 * therefore different golden files.
 */
export const PNT_ENGAGEMENT_TN = Object.freeze({
    NOMINAL: null,
    DEGRADED: 11,
    OFFLINE: 17,
});
// ---------------------------------------------------------------------------
// Missile Warning release → interception modifier (RB §5.3.6)
// ---------------------------------------------------------------------------
/**
 * The price of waiting. Releasing early is worth more to Joint Fires, and getting
 * it wrong costs more than getting it late.
 */
export const RELEASE_STAGE_MODIFIER = Object.freeze({
    1: 4,
    2: 3,
    3: 2,
    4: 0,
});
/** Wrong model within the right category (RB §5.3.6). */
export const MISCLASSIFICATION_SAME_CATEGORY = -2;
/** Wrong category entirely: the penalty applies *and* the stage bonus is forfeit. */
export const MISCLASSIFICATION_WRONG_CATEGORY = -4;
/**
 * RB §5.3.6's outcome table, as one function — **keyed on the model** (M4-D2).
 *
 * The three rows, in order:
 *
 *  - the exact catalogue row MW named is the one that flew → the stage bonus;
 *  - a different row in the same category → the stage bonus and `[-2]`. This is the
 *    row that was unreachable before M4, when there were seven identities and each
 *    *was* its category;
 *  - a different category, or an identity no catalogue can resolve → `[-4]` and the
 *    bonus is forfeit. An identity nobody can resolve is not a correct call.
 *
 * A `DISMISS` never reaches here, and there is deliberately **no branch for one**.
 * Phase 7 refuses any non-`RELEASE` disposition before consulting the modifier,
 * because RB §5.3.6 says a dismissed threat cannot be intercepted at all rather
 * than intercepted badly. M4 §11.7 recorded the guard this function used to carry
 * as unreachable; it is deleted rather than left sitting here untested, so the one
 * place that decides a dismissal is phase 7's eligibility check.
 */
export function interceptModifierFor(release, trueModel, catalogue) {
    const stage = RELEASE_STAGE_MODIFIER[release.releasedAtStage];
    if (release.assignedIdentity === trueModel)
        return stage;
    const called = mwCategoryOf(catalogue, release.assignedIdentity);
    const actual = mwCategoryOf(catalogue, trueModel);
    if (called !== null && actual !== null && called === actual) {
        return stage + MISCLASSIFICATION_SAME_CATEGORY;
    }
    return MISCLASSIFICATION_WRONG_CATEGORY;
}
/**
 * The stage bonus alone (RB §5.3.6's first column).
 *
 * This is the only half of the modifier Joint Fires may see before committing
 * (decision M4-D7): the total is computed against the launch's *true* model, so
 * showing it would tell FIRES whether MW got the call right — a free check on
 * another cell's work, and most of the reason the MW cell exists.
 */
export function releaseStageModifierFor(release) {
    if (release.disposition === 'DISMISS')
        return 0;
    return RELEASE_STAGE_MODIFIER[release.releasedAtStage];
}
export function weaponFamily(type) {
    switch (type) {
        case 'SRBM':
        case 'IRBM':
        case 'HGV':
        case 'ICBM':
            return 'BALLISTIC';
        case 'DA_ASAT':
            return 'DIRECT_ASCENT';
        case 'SPACE_LAUNCH':
        case 'TEST_OR_EXERCISE':
        case 'BENIGN':
            return 'BENIGN';
        default:
            return 'OTHER';
    }
}
export const WEAPON_TABLE_MANIFEST = {
    WEAPONS,
    MAX_INTERCEPT_SALVO,
    DEBRIS_ON_DESTRUCTION,
    DEBRIS_LARGE_SIGNATURE_BONUS,
    PNT_ENGAGEMENT_TN,
    RELEASE_STAGE_MODIFIER,
    MISCLASSIFICATION_SAME_CATEGORY,
    MISCLASSIFICATION_WRONG_CATEGORY,
};
//# sourceMappingURL=weapons.js.map